/**
 * Knowing whether a member already has a password, and remembering that they said no.
 *
 * **There is no platform signal for "this account has a password."** The session token
 * does not carry one — the claims the access-token hook writes describe entitlements
 * and a person identifier, nothing about credentials — and the authentication service
 * never writes one into account metadata either. Supabase keeps the credential in a
 * table a client cannot read, and deliberately so.
 *
 * So the portal records it itself, at the one moment it knows for certain: the same
 * call that sets the password also writes this flag. That is sound today because the
 * portal is the *only* thing in the estate that ever sets a Sleepio password — nothing
 * server-side writes one — so every password that exists will have been set through a
 * screen that writes the flag alongside it.
 *
 * Two things follow, and both are worth knowing before relying on this.
 *
 * It is a hint, never a control. A member can edit their own account metadata through
 * a self-service endpoint, so a determined person could clear the flag. All that buys
 * them is being asked a question they can already answer, or not being asked one they
 * could have declined. Nothing about access depends on it.
 *
 * And if anything server-side ever starts setting passwords, it has to write this same
 * flag or members will be asked to set a password they already have.
 */

/** Account-metadata key recording that this member has set a password. */
export const HAS_PASSWORD_METADATA_KEY = 'has_password';

/** Per-tab record that the member said "not now" — so they are not asked twice. */
export const PASSWORD_PROMPT_DECLINED_KEY = 'cap_password_prompt_declined';

interface UserLike {
  user_metadata?: Record<string, unknown> | null;
}

export const memberHasPassword = (user?: UserLike | null): boolean => (
  user?.user_metadata?.[HAS_PASSWORD_METADATA_KEY] === true
);

export const hasDeclinedPasswordPrompt = (): boolean => {
  try {
    return sessionStorage.getItem(PASSWORD_PROMPT_DECLINED_KEY) === 'true';
  } catch {
    // Storage can be unavailable in a locked-down browser. Falling back to "not
    // declined" means the worst case is being asked again, never being locked out.
    return false;
  }
};

export const rememberPasswordPromptDeclined = (): void => {
  try {
    sessionStorage.setItem(PASSWORD_PROMPT_DECLINED_KEY, 'true');
  } catch {
    // Same reasoning as above — nothing here is worth failing a sign-in over.
  }
};

/**
 * Ask the question only when both are true: this member has no password, and they
 * have not already waved it away in this session.
 */
export const shouldAskAboutPassword = (user?: UserLike | null): boolean => (
  !memberHasPassword(user) && !hasDeclinedPasswordPrompt()
);
